// Declarative registry for EVERMIN Smart Calculators.
// Each calculator declares fields + a pure compute(). Labels are bilingual inline.
import {
  PiggyBank, Landmark, Scale, Percent, Tag, Receipt, TrendingUp, Target,
  ShoppingCart, GitCompare, CalendarDays, Clock, Cake, Users, Fuel, Gauge, Map, LineChart,
} from "lucide-react";
import {
  calcSimpleInterest, calcCompoundInterest, calcPercentage, calcPercentageChange,
  calcDiscount, calcGst, calcUnitPrice, calcPriceDifference, calcMonthlyAnnual,
  calcAge, calcDateDifference, calcTimeDifference, calcFuelCost, calcMileage, calcTripCost,
} from "@/lib/calculators";

const pct = (x) => `${x}%`;

export const GROUPS = [
  { id: "money", name: { en: "Money", hi: "पैसा" } },
  { id: "shopping", name: { en: "Shopping", hi: "खरीदारी" } },
  { id: "daily", name: { en: "Daily Life", hi: "दैनिक जीवन" } },
  { id: "travel", name: { en: "Travel", hi: "यात्रा" } },
];

// Custom-UI calculators (rendered by dedicated components in Calculators.jsx)
export const CUSTOM = {
  budget: { group: "money", icon: PiggyBank, name: { en: "Budget Planner", hi: "बजट प्लानर" } },
  investment: { group: "money", icon: LineChart, name: { en: "Investment / SIP", hi: "निवेश / SIP" } },
  "savings-goal": { group: "money", icon: Target, name: { en: "Smart Savings Goal", hi: "स्मार्ट बचत लक्ष्य" } },
  emi: { group: "money", icon: Landmark, name: { en: "EMI / Loan", hi: "EMI / लोन" } },
  "rent-vs-buy": { group: "money", icon: Scale, name: { en: "Rent vs Buy", hi: "किराया बनाम खरीद" } },
};

export const REGISTRY = {
  "simple-interest": {
    group: "money", icon: Percent, name: { en: "Simple Interest", hi: "साधारण ब्याज" },
    fields: [
      { key: "principal", label: { en: "Principal", hi: "मूलधन" }, default: 100000, suffix: "₹" },
      { key: "rate", label: { en: "Rate (% per year)", hi: "दर (% प्रति वर्ष)" }, default: 8, suffix: "%" },
      { key: "years", label: { en: "Years", hi: "वर्ष" }, default: 5 },
    ],
    compute: (v, fmt) => {
      const r = calcSimpleInterest(v);
      const yrs = Number(v.years) || 1;
      return { rows: [
        { label: { en: "Interest", hi: "ब्याज" }, value: fmt(r.interest), big: true },
        { label: { en: "Per year", hi: "प्रति वर्ष" }, value: fmt(Math.round(r.interest / (yrs || 1))) },
        { label: { en: "Per month", hi: "प्रति माह" }, value: fmt(Math.round(r.interest / ((yrs || 1) * 12))), accent: true },
        { label: { en: "Total amount", hi: "कुल राशि" }, value: fmt(r.total) },
      ]};
    },
  },
  "compound-interest": {
    group: "money", icon: TrendingUp, name: { en: "Compound Interest", hi: "चक्रवृद्धि ब्याज" },
    fields: [
      { key: "principal", label: { en: "Initial amount", hi: "प्रारंभिक राशि" }, default: 100000, suffix: "₹" },
      { key: "monthly", label: { en: "Monthly contribution", hi: "मासिक योगदान" }, default: 5000, suffix: "₹" },
      { key: "rate", label: { en: "Rate (% per year)", hi: "दर (% प्रति वर्ष)" }, default: 8, suffix: "%" },
      { key: "years", label: { en: "Years", hi: "वर्ष" }, default: 10 },
      { key: "timesPerYear", label: { en: "Compounds per year", hi: "प्रति वर्ष चक्र" }, default: 4 },
      { key: "inflation", label: { en: "Inflation (% per year)", hi: "महंगाई (% प्रति वर्ष)" }, default: 6, suffix: "%" },
    ],
    compute: (v, fmt) => {
      const r = calcCompoundInterest(v);
      return { rows: [
        { label: { en: "Future value", hi: "भविष्य मूल्य" }, value: fmt(r.total), big: true },
        { label: { en: "Total invested", hi: "कुल निवेश" }, value: fmt(r.invested) },
        { label: { en: "Interest earned", hi: "अर्जित ब्याज" }, value: fmt(r.interest), accent: true },
        { label: { en: "Value in today's money", hi: "आज के मूल्य में" }, value: fmt(r.realValue) },
      ]};
    },
  },
  percentage: {
    group: "money", icon: Percent, name: { en: "Percentage", hi: "प्रतिशत" },
    fields: [
      { key: "value", label: { en: "Value", hi: "मान" }, default: 2500 },
      { key: "percent", label: { en: "Percent of", hi: "का प्रतिशत" }, default: 18, suffix: "%" },
    ],
    compute: (v) => {
      const r = calcPercentage(v);
      const val = Number(v.value) || 0;
      return { rows: [
        { label: { en: `${r.percent}% of ${r.value}`, hi: `${r.value} का ${r.percent}%` }, value: String(r.result), big: true },
        { label: { en: "Value + %", hi: "मान + %" }, value: String(Math.round((val + r.result) * 100) / 100) },
        { label: { en: "Value − %", hi: "मान − %" }, value: String(Math.round((val - r.result) * 100) / 100), accent: true },
      ]};
    },
  },
  discount: {
    group: "shopping", icon: Tag, name: { en: "Discount", hi: "छूट" },
    fields: [
      { key: "price", label: { en: "Original price", hi: "मूल कीमत" }, default: 4999, suffix: "₹" },
      { key: "discountPercent", label: { en: "Discount", hi: "छूट" }, default: 25, suffix: "%" },
    ],
    compute: (v, fmt) => {
      const r = calcDiscount(v);
      const price = Number(v.price) || 0;
      const payPct = price > 0 ? Math.round((r.final / price) * 100) : 0;
      return {
        rows: [
          { label: { en: "Original price", hi: "मूल कीमत" }, value: fmt(price) },
          { label: { en: "You save", hi: "आप बचाते हैं" }, value: fmt(r.saved), accent: true },
          { label: { en: "Final price", hi: "अंतिम कीमत" }, value: fmt(r.final), big: true },
        ],
        verdict: { en: `You pay just ${payPct}% of the original price`, hi: `आप मूल कीमत का केवल ${payPct}% देते हैं` },
      };
    },
  },
  gst: {
    group: "shopping", icon: Receipt, name: { en: "GST Calculator", hi: "GST कैलकुलेटर" },
    fields: [
      { key: "amount", label: { en: "Amount", hi: "राशि" }, default: 1000, suffix: "₹" },
      { key: "gstPercent", label: { en: "GST rate", hi: "GST दर" }, default: 18, suffix: "%", type: "select",
        options: [
          { value: 5, label: { en: "5%", hi: "5%" } }, { value: 12, label: { en: "12%", hi: "12%" } },
          { value: 18, label: { en: "18%", hi: "18%" } }, { value: 28, label: { en: "28%", hi: "28%" } },
        ] },
      { key: "inclusive", label: { en: "Amount already includes GST", hi: "राशि में GST शामिल है" }, default: false, type: "toggle" },
    ],
    compute: (v, fmt) => {
      const r = calcGst(v);
      return { rows: [
        { label: { en: "Base amount", hi: "मूल राशि" }, value: fmt(r.base) },
        { label: { en: "CGST (half)", hi: "CGST (आधा)" }, value: fmt(Math.round(r.gst / 2)) },
        { label: { en: "SGST (half)", hi: "SGST (आधा)" }, value: fmt(r.gst - Math.round(r.gst / 2)) },
        { label: { en: "Total GST", hi: "कुल GST" }, value: fmt(r.gst), accent: true },
        { label: { en: "Total", hi: "कुल" }, value: fmt(r.total), big: true },
      ]};
    },
  },
  "discount-gst": {
    group: "shopping", icon: Receipt, name: { en: "Discount + GST", hi: "छूट + GST" },
    fields: [
      { key: "price", label: { en: "Price", hi: "कीमत" }, default: 4999, suffix: "₹" },
      { key: "discountPercent", label: { en: "Discount", hi: "छूट" }, default: 20, suffix: "%" },
      { key: "gstPercent", label: { en: "GST", hi: "GST" }, default: 18, suffix: "%" },
      { key: "qty", label: { en: "Quantity", hi: "मात्रा" }, default: 1 },
    ],
    compute: (v, fmt) => {
      const qty = Math.max(1, Number(v.qty) || 1);
      const d = calcDiscount(v);
      const g = calcGst({ amount: d.final, gstPercent: v.gstPercent, inclusive: false });
      const mrpGst = calcGst({ amount: Number(v.price) || 0, gstPercent: v.gstPercent, inclusive: false });
      const saved = mrpGst.total - g.total;
      return {
        rows: [
          { label: { en: "After discount", hi: "छूट के बाद" }, value: fmt(d.final) },
          { label: { en: "GST added", hi: "जोड़ा गया GST" }, value: fmt(g.gst) },
          { label: { en: "Price per unit", hi: "प्रति यूनिट कीमत" }, value: fmt(g.total) },
          { label: { en: `You pay (x${qty})`, hi: `आप देते हैं (x${qty})` }, value: fmt(g.total * qty), big: true },
        ],
        verdict: { en: `You save ${fmt(saved * qty)} vs MRP + GST`, hi: `MRP + GST की तुलना में ${fmt(saved * qty)} बचत` },
      };
    },
  },
  "unit-price": {
    group: "shopping", icon: ShoppingCart, name: { en: "Unit Price Compare", hi: "यूनिट कीमत तुलना" },
    fields: [
      { key: "priceA", label: { en: "Pack A price", hi: "पैक A कीमत" }, default: 199, suffix: "₹" },
      { key: "qtyA", label: { en: "Pack A quantity", hi: "पैक A मात्रा" }, default: 500 },
      { key: "priceB", label: { en: "Pack B price", hi: "पैक B कीमत" }, default: 349, suffix: "₹" },
      { key: "qtyB", label: { en: "Pack B quantity", hi: "पैक B मात्रा" }, default: 1000 },
    ],
    compute: (v, fmt) => {
      const r = calcUnitPrice(v);
        const better = Math.min(r.unitA, r.unitB);
        const worse = Math.max(r.unitA, r.unitB);
        const pctCheaper = worse > 0 ? Math.round(((worse - better) / worse) * 100) : 0;
      return {
        rows: [
          { label: { en: "Pack A per unit", hi: "पैक A प्रति यूनिट" }, value: fmt(r.unitA) },
          { label: { en: "Pack B per unit", hi: "पैक B प्रति यूनिट" }, value: fmt(r.unitB) },
        ],
        verdict: r.cheaper === "equal" ? { en: "Both cost the same per unit", hi: "दोनों प्रति यूनिट बराबर हैं" }
          : { en: `Pack ${r.cheaper.toUpperCase()} is ${pctCheaper}% cheaper per unit`, hi: `पैक ${r.cheaper.toUpperCase()} प्रति यूनिट ${pctCheaper}% सस्ता है` },
      };
    },
  },
  "price-difference": {
    group: "shopping", icon: GitCompare, name: { en: "Price Difference", hi: "कीमत अंतर" },
    fields: [
      { key: "priceA", label: { en: "Price A", hi: "कीमत A" }, default: 1200, suffix: "₹" },
      { key: "priceB", label: { en: "Price B", hi: "कीमत B" }, default: 1500, suffix: "₹" },
    ],
    compute: (v, fmt) => {
      const r = calcPriceDifference(v);
      const a = Number(v.priceA) || 0, b = Number(v.priceB) || 0;
      return {
        rows: [
          { label: { en: "Difference", hi: "अंतर" }, value: fmt(r.difference), big: true },
          { label: { en: "Difference %", hi: "अंतर %" }, value: pct(r.percent), accent: true },
        ],
        verdict: a === b ? { en: "Both prices are equal", hi: "दोनों कीमतें बराबर हैं" }
          : { en: `Option ${a < b ? "A" : "B"} is cheaper by ${fmt(r.difference)}`, hi: `विकल्प ${a < b ? "A" : "B"} ${fmt(r.difference)} सस्ता है` },
      };
    },
  },
  "monthly-annual": {
    group: "shopping", icon: CalendarDays, name: { en: "Monthly / Annual Cost", hi: "मासिक / वार्षिक लागत" },
    fields: [
      { key: "amount", label: { en: "Amount", hi: "राशि" }, default: 499, suffix: "₹" },
      { key: "period", label: { en: "This amount is", hi: "यह राशि है" }, default: "monthly", type: "select",
        options: [
          { value: "monthly", label: { en: "Per month", hi: "प्रति माह" } },
          { value: "annual", label: { en: "Per year", hi: "प्रति वर्ष" } },
        ] },
    ],
    compute: (v, fmt) => {
      const r = calcMonthlyAnnual(v);
      return { rows: [
        { label: { en: "Per month", hi: "प्रति माह" }, value: fmt(r.monthly), big: true },
        { label: { en: "Per week", hi: "प्रति सप्ताह" }, value: fmt(Math.round(r.annual / 52)) },
        { label: { en: "Per day", hi: "प्रति दिन" }, value: fmt(Math.round(r.annual / 365)) },
        { label: { en: "Per year", hi: "प्रति वर्ष" }, value: fmt(r.annual), accent: true },
      ]};
    },
  },
  age: {
    group: "daily", icon: Cake, name: { en: "Age Calculator", hi: "आयु कैलकुलेटर" },
    fields: [{ key: "dob", label: { en: "Date of birth", hi: "जन्म तिथि" }, type: "date", default: "2000-01-01" }],
    compute: (v) => {
      const r = calcAge(v);
      return { rows: [
        { label: { en: "Age", hi: "आयु" }, value: `${r.years}y ${r.months}m ${r.days}d`, big: true },
        { label: { en: "Total months", hi: "कुल महीने" }, value: String(r.years * 12 + r.months), accent: true },
        { label: { en: "Total weeks", hi: "कुल सप्ताह" }, value: String(Math.floor((r.totalDays || 0) / 7)) },
        { label: { en: "Total days", hi: "कुल दिन" }, value: String(r.totalDays || 0) },
      ]};
    },
  },
  "date-difference": {
    group: "daily", icon: CalendarDays, name: { en: "Date Difference", hi: "तिथि अंतर" },
    fields: [
      { key: "from", label: { en: "From date", hi: "से तिथि" }, type: "date", default: "2026-01-01" },
      { key: "to", label: { en: "To date", hi: "तक तिथि" }, type: "date", default: "2026-12-31" },
    ],
    compute: (v) => {
      const r = calcDateDifference(v);
      return { rows: [
        { label: { en: "Days", hi: "दिन" }, value: String(r.days), big: true },
        { label: { en: "Weeks", hi: "सप्ताह" }, value: String(r.weeks), accent: true },
        { label: { en: "Months", hi: "महीने" }, value: String(r.months) },
        { label: { en: "Years (approx)", hi: "वर्ष (लगभग)" }, value: String(Math.round((r.days / 365) * 10) / 10) },
      ]};
    },
  },
  "time-difference": {
    group: "daily", icon: Clock, name: { en: "Time Difference", hi: "समय अंतर" },
    fields: [
      { key: "start", label: { en: "Start time", hi: "आरंभ समय" }, type: "time", default: "09:00" },
      { key: "end", label: { en: "End time", hi: "समाप्ति समय" }, type: "time", default: "17:30" },
    ],
    compute: (v) => {
      const r = calcTimeDifference(v);
      const decimal = Math.round((r.totalMinutes / 60) * 100) / 100;
      const workPct = Math.round((r.totalMinutes / 480) * 100);
      return { rows: [
        { label: { en: "Duration", hi: "अवधि" }, value: `${r.hours}h ${r.minutes}m`, big: true },
        { label: { en: "Decimal hours", hi: "दशमलव घंटे" }, value: `${decimal} h`, accent: true },
        { label: { en: "Total minutes", hi: "कुल मिनट" }, value: String(r.totalMinutes) },
        { label: { en: "Of an 8h workday", hi: "8 घंटे के दिन का" }, value: `${workPct}%` },
      ]};
    },
  },
  "percentage-change": {
    group: "daily", icon: TrendingUp, name: { en: "Percentage Change", hi: "प्रतिशत परिवर्तन" },
    fields: [
      { key: "oldValue", label: { en: "Old value", hi: "पुराना मान" }, default: 200 },
      { key: "newValue", label: { en: "New value", hi: "नया मान" }, default: 250 },
    ],
    compute: (v) => {
      const r = calcPercentageChange(v);
      const o = Number(v.oldValue) || 0, nv = Number(v.newValue) || 0;
      const mult = o !== 0 ? Math.round((nv / o) * 100) / 100 : 0;
      return {
        rows: [
          { label: { en: "Change", hi: "परिवर्तन" }, value: `${r.change > 0 ? "+" : ""}${r.change}%`, big: true },
          { label: { en: "Difference", hi: "अंतर" }, value: String(r.difference), accent: true },
          { label: { en: "Multiplier", hi: "गुणक" }, value: `${mult}x` },
        ],
        verdict: r.change === 0 ? { en: "No change", hi: "कोई परिवर्तन नहीं" }
          : { en: `${r.direction === "up" ? "Increased" : "Decreased"} by ${Math.abs(r.change)}%`, hi: `${Math.abs(r.change)}% ${r.direction === "up" ? "वृद्धि" : "कमी"}` },
      };
    },
  },
  "split-bill": {
    group: "daily", icon: Users, name: { en: "Split Bill", hi: "बिल बाँटें" },
    fields: [
      { key: "total", label: { en: "Bill amount", hi: "बिल राशि" }, default: 2400, suffix: "₹" },
      { key: "people", label: { en: "People", hi: "लोग" }, default: 4 },
      { key: "tipPercent", label: { en: "Tip", hi: "टिप" }, default: 5, suffix: "%" },
      { key: "taxPercent", label: { en: "Tax / GST", hi: "कर / GST" }, default: 5, suffix: "%" },
    ],
    compute: (v, fmt) => {
      const total = Number(v.total) || 0;
      const people = Math.max(1, Number(v.people) || 1);
      const tip = total * (Number(v.tipPercent) || 0) / 100;
      const tax = total * (Number(v.taxPercent) || 0) / 100;
      const grand = total + tip + tax;
      return { rows: [
        { label: { en: "Bill", hi: "बिल" }, value: fmt(total) },
        { label: { en: "Tip", hi: "टिप" }, value: fmt(Math.round(tip)) },
        { label: { en: "Tax / GST", hi: "कर / GST" }, value: fmt(Math.round(tax)) },
        { label: { en: "Grand total", hi: "कुल योग" }, value: fmt(Math.round(grand)), accent: true },
        { label: { en: `Per person (x${people})`, hi: `प्रति व्यक्ति (x${people})` }, value: fmt(Math.round(grand / people)), big: true },
      ]};
    },
  },
  "fuel-cost": {
    group: "travel", icon: Fuel, name: { en: "Fuel Cost", hi: "ईंधन लागत" },
    fields: [
      { key: "distance", label: { en: "One-way distance (km)", hi: "एक-तरफ़ा दूरी (किमी)" }, default: 300 },
      { key: "mileage", label: { en: "Mileage (km/L)", hi: "माइलेज (किमी/ली)" }, default: 18 },
      { key: "fuelPrice", label: { en: "Fuel price / L", hi: "ईंधन कीमत / ली" }, default: 100, suffix: "₹" },
      { key: "roundTrip", label: { en: "Round trip", hi: "वापसी यात्रा" }, default: false, type: "toggle" },
      { key: "passengers", label: { en: "Passengers (to split)", hi: "यात्री (बाँटने हेतु)" }, default: 1 },
    ],
    compute: (v, fmt) => {
      const dist = (Number(v.distance) || 0) * (v.roundTrip ? 2 : 1);
      const r = calcFuelCost({ distance: dist, mileage: v.mileage, fuelPrice: v.fuelPrice });
      const people = Math.max(1, Number(v.passengers) || 1);
      const perKm = dist > 0 ? Math.round((r.cost / dist) * 100) / 100 : 0;
      return { rows: [
        { label: { en: "Fuel needed", hi: "ज़रूरी ईंधन" }, value: `${r.litres} L` },
        { label: { en: "Fuel cost", hi: "ईंधन लागत" }, value: fmt(r.cost), big: true },
        { label: { en: "Cost per km", hi: "प्रति किमी लागत" }, value: fmt(perKm), accent: true },
        { label: { en: `Per passenger (x${people})`, hi: `प्रति यात्री (x${people})` }, value: fmt(Math.round(r.cost / people)) },
      ]};
    },
  },
  mileage: {
    group: "travel", icon: Gauge, name: { en: "Mileage", hi: "माइलेज" },
    fields: [
      { key: "distance", label: { en: "Distance (km)", hi: "दूरी (किमी)" }, default: 400 },
      { key: "litres", label: { en: "Fuel used (L)", hi: "इस्तेमाल ईंधन (ली)" }, default: 22 },
      { key: "fuelPrice", label: { en: "Fuel price / L", hi: "ईंधन कीमत / ली" }, default: 100, suffix: "₹" },
      { key: "monthlyKm", label: { en: "Monthly km (optional)", hi: "मासिक किमी (वैकल्पिक)" }, default: 1000 },
    ],
    compute: (v, fmt) => {
      const r = calcMileage(v);
      const price = Number(v.fuelPrice) || 0;
      const costPerKm = r.mileage > 0 ? Math.round((price / r.mileage) * 100) / 100 : 0;
      const monthly = Math.round(costPerKm * (Number(v.monthlyKm) || 0));
      return { rows: [
        { label: { en: "Mileage", hi: "माइलेज" }, value: `${r.mileage} km/L`, big: true },
        { label: { en: "Cost per km", hi: "प्रति किमी लागत" }, value: fmt(costPerKm), accent: true },
        { label: { en: "Est. monthly fuel", hi: "अनुमानित मासिक ईंधन" }, value: fmt(monthly) },
        { label: { en: "Est. yearly fuel", hi: "अनुमानित वार्षिक ईंधन" }, value: fmt(monthly * 12) },
      ]};
    },
  },
  "trip-cost": {
    group: "travel", icon: Map, name: { en: "Trip Cost", hi: "यात्रा लागत" },
    fields: [
      { key: "distance", label: { en: "One-way distance (km)", hi: "एक-तरफ़ा दूरी (किमी)" }, default: 500 },
      { key: "mileage", label: { en: "Mileage (km/L)", hi: "माइलेज (किमी/ली)" }, default: 18 },
      { key: "fuelPrice", label: { en: "Fuel price / L", hi: "ईंधन कीमत / ली" }, default: 100, suffix: "₹" },
      { key: "tolls", label: { en: "Tolls", hi: "टोल" }, default: 200, suffix: "₹" },
      { key: "other", label: { en: "Stay / food / other", hi: "ठहराव / भोजन / अन्य" }, default: 0, suffix: "₹" },
      { key: "roundTrip", label: { en: "Round trip", hi: "वापसी यात्रा" }, default: true, type: "toggle" },
      { key: "passengers", label: { en: "Passengers", hi: "यात्री" }, default: 2 },
    ],
    compute: (v, fmt) => {
      const dist = (Number(v.distance) || 0) * (v.roundTrip ? 2 : 1);
      const r = calcTripCost({ distance: dist, mileage: v.mileage, fuelPrice: v.fuelPrice, tolls: v.tolls, other: v.other });
      const people = Math.max(1, Number(v.passengers) || 1);
      return { rows: [
        { label: { en: "Fuel cost", hi: "ईंधन लागत" }, value: fmt(r.fuelCost) },
        { label: { en: "Tolls + other", hi: "टोल + अन्य" }, value: fmt((Number(v.tolls) || 0) + (Number(v.other) || 0)) },
        { label: { en: "Total trip cost", hi: "कुल यात्रा लागत" }, value: fmt(r.total), big: true },
        { label: { en: `Per person (x${people})`, hi: `प्रति व्यक्ति (x${people})` }, value: fmt(Math.round(r.total / people)), accent: true },
      ]};
    },
  },
};

export function calcById(id) {
  return REGISTRY[id] || null;
}
